(() => {
  // Idempotent global
  if (window.DYCEnhancedATC) return;

  const state = {
    extrasBySection: new Map(), // sectionId -> { count, cents }
  };

  const watchers = {
    priceObserverBySection: new Map(), // sectionId -> MutationObserver
    domObserver: null,
    updateTimers: new Map(), // sectionId -> timer
  };

  function formatMoney(cents) {
    if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    return "$" + (cents / 100).toFixed(2);
  }

  function parsePriceCentsFromText(text) {
    if (!text) return null;
    const match = String(text).match(/[\d,]+(?:\.\d{1,2})?/);
    if (!match) return null;
    const num = parseFloat(match[0].replace(/,/g, ""));
    if (Number.isNaN(num)) return null;
    return Math.round(num * 100);
  }

  function getScope(sectionId) {
    const byMain = document.getElementById(`MainProduct-${sectionId}`);
    if (byMain) return byMain;

    const pf = document.querySelector(
      `product-form[data-section="${sectionId}"], mcc-product-form[data-section="${sectionId}"]`,
    );
    const wrap = pf && pf.closest ? pf.closest(".shopify-section") : null;
    return wrap || document;
  }

  function getFormId(scope, sectionId) {
    const pf =
      scope.querySelector(
        `product-form[data-section="${sectionId}"], mcc-product-form[data-section="${sectionId}"]`,
      ) ||
      document.querySelector(
        `product-form[data-section="${sectionId}"], mcc-product-form[data-section="${sectionId}"]`,
      );

    const form = pf && pf.querySelector ? pf.querySelector("form[id]") : null;
    if (form && form.id) return form.id;

    const any =
      scope.querySelector('form[id^="product-form-"][data-type="add-to-cart-form"]') ||
      scope.querySelector('form[id^="product-form-"]');
    return any && any.id ? any.id : null;
  }

  function getQty(scope, formId, sectionId) {
    const selectors = [
      formId ? `input[name="quantity"][form="${CSS.escape(formId)}"]` : null,
      `#Quantity-${CSS.escape(sectionId)}`,
      `#Qty-${CSS.escape(sectionId)}`,
      formId ? `form#${CSS.escape(formId)} input[name="quantity"]` : null,
      `product-form[data-section="${CSS.escape(sectionId)}"] input[name="quantity"]`,
      `mcc-product-form[data-section="${CSS.escape(sectionId)}"] input[name="quantity"]`,
      `input[name="quantity"]`,
    ].filter(Boolean);

    for (const sel of selectors) {
      const el = scope.querySelector(sel);
      if (!el) continue;
      const qty = Math.max(1, parseInt(el.value || "1", 10));
      return Number.isFinite(qty) ? qty : 1;
    }
    return 1;
  }

  function getVariantPriceCents(scope, sectionId, variant) {
    // Prefer variant JSON (cents)
    if (variant && typeof variant.price === "number" && Number.isFinite(variant.price)) {
      scope.dataset.dycVariantPriceCents = String(variant.price);
      return variant.price;
    }

    // Parse from price markup
    const priceEl =
      scope.querySelector(`#Price-${CSS.escape(sectionId)} .price-item--sale`) ||
      scope.querySelector(`#Price-${CSS.escape(sectionId)} .price-item--regular`) ||
      scope.querySelector(`#Price-${CSS.escape(sectionId)}`) ||
      scope.querySelector('[id^="Price-"] .price-item--sale') ||
      scope.querySelector('[id^="Price-"] .price-item--regular') ||
      scope.querySelector('[id^="Price-"]');

    const cents = parsePriceCentsFromText(priceEl && priceEl.textContent);
    if (typeof cents === "number" && cents > 0) {
      scope.dataset.dycVariantPriceCents = String(cents);
      return cents;
    }

    // Last resort: use stored value (if we previously had it)
    const stored = parseInt(scope.dataset.dycVariantPriceCents || "", 10);
    if (Number.isFinite(stored) && stored > 0) return stored;

    return 0;
  }

  function scheduleUpdate(sectionId, variant) {
    if (!sectionId) return;
    const sid = String(sectionId);
    const existing = watchers.updateTimers.get(sid);
    if (existing) clearTimeout(existing);
    const t = setTimeout(() => {
      watchers.updateTimers.delete(sid);
      updateSection(sid, variant || null);
    }, 0);
    watchers.updateTimers.set(sid, t);
  }

  function setupPriceObserver(sectionId) {
    if (!sectionId) return;
    const sid = String(sectionId);
    if (watchers.priceObserverBySection.has(sid)) return;

    const scope = getScope(sid);
    const priceRoot =
      scope.querySelector(`#Price-${CSS.escape(sid)}`) ||
      scope.querySelector('[id^="Price-"]');
    if (!priceRoot) return;

    const obs = new MutationObserver(() => {
      // Ensure we re-parse from DOM (don’t get stuck on a cached value)
      try {
        delete scope.dataset.dycVariantPriceCents;
      } catch (_) {}
      scheduleUpdate(sid, null);
    });

    obs.observe(priceRoot, { childList: true, subtree: true, characterData: true });
    watchers.priceObserverBySection.set(sid, obs);
  }

  function findButtons(scope, formId, sectionId) {
    const buttons = new Set();

    const byId = document.getElementById(`Product-Submit-Button-${sectionId}`);
    if (byId) buttons.add(byId);

    // External submit buttons linked by form="..."
    if (formId) {
      scope
        .querySelectorAll(`button[type="submit"][form="${CSS.escape(formId)}"]`)
        .forEach((b) => buttons.add(b));
    }

    // Buttons inside the form itself
    if (formId) {
      const form = scope.querySelector(`form#${CSS.escape(formId)}`) || document.getElementById(formId);
      if (form) {
        form
          .querySelectorAll(
            ".add-cart-button, .fd-add-cart-button, .ckb-add-cart-button, button[type=\"submit\"]",
          )
          .forEach((b) => buttons.add(b));
      }
    }

    // Fallbacks in scope
    scope
      .querySelectorAll(".add-cart-button, .fd-add-cart-button, .ckb-add-cart-button")
      .forEach((b) => buttons.add(b));

    return Array.from(buttons);
  }

  function renderEnhanced(btn, totalQty, formattedPrice) {
    // Preserve loading overlay if present
    const overlay = btn.querySelector(".loading-overlay");
    const overlayHtml = overlay ? overlay.outerHTML : "";

    // Preserve the current label + classes from .button-text. Dedupe and drop
    // "atc-text" first — this runs on every qty/variant change and re-reads the
    // class list it wrote last time, which otherwise accumulates copies.
    const currentTextEl = btn.querySelector(".button-text");
    const label = currentTextEl ? currentTextEl.textContent.trim() : "Add to Cart";
    const textClasses = (currentTextEl ? currentTextEl.className : "button-text")
      .split(/\s+/)
      .filter((cls, i, all) => cls && cls !== "atc-text" && all.indexOf(cls) === i)
      .join(" ");

    btn.classList.add("atc-enhanced");
    btn.innerHTML = `
      <span class="atc-qty">${totalQty}</span>
      <span class="${textClasses} atc-text">${label}</span>
      <span class="atc-price">${formattedPrice}</span>
      ${overlayHtml}
    `;
  }

  function updateSection(sectionId, variant) {
    if (!sectionId) return;

    const sid = String(sectionId);
    const scope = getScope(sid);
    setupPriceObserver(sid);
    const formId = getFormId(scope, sid);
    const qty = getQty(scope, formId, sid);

    const extras = state.extrasBySection.get(sid) || { count: 0, cents: 0 };
    const variantPrice = getVariantPriceCents(scope, sid, variant);

    const totalQty = qty + (extras.count || 0);
    const totalCents = Math.max(0, variantPrice * qty + (extras.cents || 0));
    const priceStr = formatMoney(totalCents);

    const buttons = findButtons(scope, formId, sid);
    if (!buttons.length) return;

    buttons.forEach((btn) => renderEnhanced(btn, totalQty, priceStr));
  }

  function updateFromVariantChangeEvent(variant, sectionIdHint) {
    if (sectionIdHint) {
      updateSection(sectionIdHint, variant);
      return;
    }

    const vid = variant && variant.id ? String(variant.id) : null;
    if (!vid) return;

    document
      .querySelectorAll("product-form[data-section], mcc-product-form[data-section]")
      .forEach((pf) => {
        const sectionId = pf.dataset.section;
        const input = pf.querySelector('input[name="id"], select[name="id"]');
        if (input && String(input.value) === vid) {
          updateSection(sectionId, variant);
        }
      });
  }

  function onQtyOrVariantInputChanged(e) {
    const t = e && e.target;
    if (!t) return;

    const id = t.id || "";
    const isQty = t.name === "quantity" || id.startsWith("Quantity-") || id.startsWith("Qty-");
    const isVariantId = t.name === "id";
    if (!isQty && !isVariantId) return;

    // 1) within product-form component
    const pf = t.closest ? t.closest("product-form, mcc-product-form") : null;
    if (pf && pf.dataset && pf.dataset.section) {
      updateSection(pf.dataset.section, null);
      return;
    }

    // 2) via associated form id
    const formId = t.getAttribute && t.getAttribute("form");
    if (formId) {
      const form = document.getElementById(formId);
      const pf2 = form && form.closest ? form.closest("product-form, mcc-product-form") : null;
      if (pf2 && pf2.dataset && pf2.dataset.section) {
        updateSection(pf2.dataset.section, null);
      }
    }
  }

  function init() {
    // Initial paint
    setTimeout(() => {
      document
        .querySelectorAll("product-form[data-section], mcc-product-form[data-section]")
        .forEach((pf) => updateSection(pf.dataset.section, null));
    }, 50);

    // Theme pubsub variant changes (standard PDP selectors)
    if (
      typeof window.subscribe === "function" &&
      window.PUB_SUB_EVENTS &&
      window.PUB_SUB_EVENTS.variantChange
    ) {
      window.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
        const sectionId = event && event.data ? event.data.sectionId : null;
        const variant = event && event.data ? event.data.variant : null;
        updateSection(sectionId, variant);
      });
    }

    // DOM variant changes (size selector / custom flows)
    document.addEventListener("variant:change", (e) => {
      const detail = e && e.detail ? e.detail : {};
      updateFromVariantChangeEvent(detail.variant, detail.sectionId);
    });

    // qty / variant id inputs
    document.addEventListener("input", onQtyOrVariantInputChanged, true);
    document.addEventListener("change", onQtyOrVariantInputChanged, true);

    // Accessories drawer selections (so enhanced ATC updates even when upsell widget isn't present)
    document.addEventListener("dyc:accessories-changed", (e) => {
      try {
        const detail = e && e.detail ? e.detail : {};
        const sectionId =
          detail.sectionId ||
          document.querySelector("[data-accessories-drawer]")?.dataset?.sectionId ||
          document.querySelector("[id^='MainProduct-']")?.getAttribute("data-section");
        if (!sectionId) return;

        const variantIds = Array.isArray(detail.variantIds) ? detail.variantIds : [];
        const prices = detail.prices && typeof detail.prices === "object" ? detail.prices : {};

        let cents = 0;
        variantIds.forEach((vid) => {
          const v = String(vid);
          const p = parseInt(prices[v] ?? prices[vid] ?? "0", 10);
          if (Number.isFinite(p) && p > 0) cents += p;
        });

        window.DYCEnhancedATC.setExtras(sectionId, { count: variantIds.length, cents });
        window.DYCEnhancedATC.update(sectionId);
      } catch (_) {}
    });

    document.addEventListener("dyc:accessories-clear", (e) => {
      try {
        const detail = e && e.detail ? e.detail : {};
        const sectionId =
          detail.sectionId ||
          document.querySelector("[data-accessories-drawer]")?.dataset?.sectionId;

        if (sectionId) {
          window.DYCEnhancedATC.setExtras(sectionId, { count: 0, cents: 0 });
          window.DYCEnhancedATC.update(sectionId);
        } else {
          // If we can't identify a section, clear all known extras and refresh all forms.
          state.extrasBySection.clear();
          document
            .querySelectorAll("product-form[data-section], mcc-product-form[data-section]")
            .forEach((pf) => window.DYCEnhancedATC.update(pf.dataset.section));
        }
      } catch (_) {}
    });

    // Quick Add injects product HTML dynamically; watch for new product-form nodes and initialize them.
    if (!watchers.domObserver && document.body) {
      watchers.domObserver = new MutationObserver((mutations) => {
        const sections = new Set();
        mutations.forEach((m) => {
          m.addedNodes.forEach((n) => {
            if (!n || n.nodeType !== 1) return;
            const el = /** @type {Element} */ (n);

            if (el.matches && el.matches("product-form[data-section], mcc-product-form[data-section]")) {
              if (el.dataset && el.dataset.section) sections.add(el.dataset.section);
            }

            if (el.querySelectorAll) {
              el
                .querySelectorAll("product-form[data-section], mcc-product-form[data-section]")
                .forEach((pf) => {
                  if (pf.dataset && pf.dataset.section) sections.add(pf.dataset.section);
                });
            }
          });
        });

        sections.forEach((sid) => scheduleUpdate(sid, null));
      });

      watchers.domObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  window.DYCEnhancedATC = {
    setExtras(sectionId, extras) {
      state.extrasBySection.set(String(sectionId), {
        count: extras && extras.count ? Number(extras.count) : 0,
        cents: extras && extras.cents ? Number(extras.cents) : 0,
      });
    },
    update(sectionId, variant) {
      updateSection(sectionId, variant || null);
    },
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();


